import { useState } from 'react';
import { useAuthStore } from '../../store/auth';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useI18n } from '../../i18n';
import { updateAthleteRecord } from '../../api/client';
import { inlineError } from '../../hooks/useApiCall';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter, { type ModalAction } from './ModalFooter';
import Notice from './Notice';
import AthleteFieldsGroup, { type AthleteFieldsForm } from './AthleteFieldsGroup';
import { changedCounters } from '../AthleteFormFields';
import { personName } from '../../utils/personName';

/** Minimal fields this modal reads from the athlete being edited. Both
 *  AthleteRecord and AthleteEntry (coach-list row, `source === 'record'`)
 *  satisfy this — callers gate on `source === 'record'` at runtime since
 *  only records can be edited through `updateAthleteRecord`. */
export interface EditableAthlete {
  id: string;
  first_name: string;
  last_name: string;
  gender?: string;
  birth_date?: string;
  experience_bouts?: number;
  /** NUMERIC: node-pg sends a string ("70.0"), and null before a weight. */
  current_weight_kg?: number | string | null;
  country?: string;
}

/** Shape of the payload passed to `onSaved` after a successful update. It
 *  comes from the saved row of the PATCH response, not from the form (#627):
 *  numeric fields as numbers, a blank weight as null. Same string-only keys
 *  as AthleteFieldsForm except the two numeric ones. */
export interface UpdatedAthletePayload extends Omit<AthleteFieldsForm, 'experience_bouts' | 'current_weight_kg'> {
  experience_bouts: number;
  current_weight_kg: number | null;
}

interface EditAthleteModalProps {
  athlete: EditableAthlete;
  onSaved: (updated: UpdatedAthletePayload) => void;
  onClose: () => void;
  /** #675, #693 — the record actions (Archive, Restore, Remove from team,
   *  Unlink, Merge) on the left of the footer: one action is a text button,
   *  two or more are the items of the Actions menu. Build them with the
   *  function `athleteRecordActions()`. The handler of each action asks its
   *  own question (confirm) before it writes. */
  recordActions?: ModalAction[];
}

export default function EditAthleteModal({ athlete, onSaved, onClose, recordActions }: EditAthleteModalProps) {
  const { showError, showSuccess, showWarning, showToast } = useToast();
  const { t } = useI18n();
  // #674 — only an admin or a team manager can lower the bout count. A coach
  // can keep or raise it. The name and the roles are those of the backend
  // check (`canLowerExperience` in lib/athleteExperience.ts, #406). The server
  // decides: it answers 403, and `showError()` below shows the reason.
  const canLowerExperience = useAuthStore(s => s.hasRole('admin', 'team_manager'));
  // The values at the open of the form. On save, the code compares the
  // experience and the weight with these values (#627).
  const [loaded] = useState<AthleteFieldsForm>(() => ({
    first_name: athlete.first_name || '',
    last_name: athlete.last_name || '',
    gender: athlete.gender || '',
    birth_date: athlete.birth_date ? String(athlete.birth_date).slice(0, 10) : '',
    experience_bouts: athlete.experience_bouts?.toString() || '0',
    current_weight_kg: athlete.current_weight_kg?.toString() || '',
    country: athlete.country || 'LT',
  }));
  const [form, setForm] = useState(loaded);
  // #605 — Save locks until the answer: a second tap sends no second PATCH.
  const saving = useBusy();
  // #674 — the reason of the last refused save. The toast hides after 3 s, so
  // the reason also stays in the modal until the next save.
  const [saveError, setSaveError] = useState('');

  async function save() {
    await saving.run(async () => {
      setSaveError('');
      try {
        const res = await updateAthleteRecord(athlete.id, {
          first_name: form.first_name,
          last_name: form.last_name,
          // #452 — a cleared field goes as null or as '', never undefined:
          // JSON drops an undefined key, and the toast said "updated" while
          // the old value stayed. The server refuses an empty birth date
          // (BIRTH_DATE_REQUIRED), and the error shows.
          gender: form.gender ? (form.gender as 'Male' | 'Female') : null,
          birth_date: form.birth_date,
          // #627 — the experience and the weight go only when the user
          // changed them: a verdict or a weigh-in can write them while the
          // form is open. A cleared weight goes as null (#452).
          ...changedCounters(form, loaded),
          country: form.country,
        });
        // #627 — the page gets the saved row, not the form. The row has the
        // experience of a verdict that came after the open of the form.
        const saved = res.data;
        const payload: UpdatedAthletePayload = {
          first_name: saved.first_name || '',
          last_name: saved.last_name || '',
          gender: saved.gender || '',
          birth_date: saved.birth_date ? String(saved.birth_date).slice(0, 10) : '',
          experience_bouts: saved.experience_bouts ?? 0,
          // NUMERIC: node-pg sends the weight as a string ("70.0").
          current_weight_kg: saved.current_weight_kg == null ? null : Number(saved.current_weight_kg),
          country: saved.country || '',
        };
        onSaved(payload);
        showSuccess(t.teams.athleteUpdated.replace('{name}', personName(saved.first_name, saved.last_name)));
      } catch (err) {
        // The toast and the line in the modal give the same text of `t.errors`.
        showError(err);
        setSaveError(inlineError(err));
      }
    });
  }

  return (
    <ModalBackdrop onClose={onClose} ariaLabel={t.modalLabels.editAthleteRecord}>
      <ModalHeader title={t.modalLabels.editAthleteRecord} />
      {/* The `min` is the count when the form opens. If a verdict adds 1 while
          the form is open, the server decides: the 403 names the stored count. */}
      <AthleteFieldsGroup form={form} onChange={setForm}
        minExperience={canLowerExperience ? undefined : (Number(loaded.experience_bouts) || 0)} />
      {saveError && <Notice tone="error" plain className="mt-3" data-testid="edit-athlete-error">{saveError}</Notice>}
      <ModalFooter actions={recordActions} onSave={save} onCancel={onClose}
        busy={saving.busy} />
    </ModalBackdrop>
  );
}
